import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/index";
import { Nudge } from "@/api/types";
import SimplePage from "@/components/structure/SimplePage";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { faBell, faExternalLinkAlt, faComment, faReply, faTasks } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "@/contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AppButton from "@/components/structure/AppButton";

export default function Nudges() {
  const { isAuthenticated, user } = useAuth();
  const [nudges, setNudges] = useState<Nudge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to get icon and label for nudge type
  const getNudgeTypeDisplay = (type: Nudge['type']) => {
    switch (type) {
      case 'task_response':
        return { icon: faTasks, label: 'Task Response', color: 'text-blue-600 bg-blue-100' };
      case 'task_comment':
        return { icon: faComment, label: 'Task Comment', color: 'text-green-600 bg-green-100' };
      case 'clarify_request':
        return { icon: faReply, label: 'Clarification Request', color: 'text-orange-600 bg-orange-100' };
      default:
        return { icon: faBell, label: 'Notification', color: 'text-gray-600 bg-gray-100' };
    }
  };

  useEffect(() => {
    const fetchNudges = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.get<Nudge[]>(`/users/${user.id}/nudges/`);
        setNudges(response);
      } catch (err) {
        setError("Failed to fetch nudges");
        console.error("Error fetching nudges:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchNudges();
  }, [isAuthenticated, user]);

  if (loading) {
    return (
      <ProtectedRoute>
        <SimplePage title="Your Nudges" icon={faBell}>
          <div className="flex items-center justify-center min-h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading nudges...</p>
            </div>
          </div>
        </SimplePage>
      </ProtectedRoute>
    );
  }

  if (error) {
    return (
      <ProtectedRoute>
        <SimplePage title="Your Nudges" icon={faBell}>
          <div className="text-center py-12">
            <div className="text-red-600 text-lg font-semibold mb-2">Error</div>
            <p className="text-gray-600">{error}</p>
          </div>
        </SimplePage>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <SimplePage title="Your Nudges" icon={faBell}>
        {nudges.length === 0 ? (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faBell} className="text-gray-400 text-4xl mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No nudges yet</h3>
            <p className="text-gray-600">You don't have any notifications at the moment.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {nudges.map((nudge, index) => {
              const typeDisplay = getNudgeTypeDisplay(nudge.type);
              return (
                <div
                  key={`nudge-${index}`}
                  className="border border-gray-300 rounded-lg p-4 bg-white shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${typeDisplay.color}`}>
                        <FontAwesomeIcon icon={typeDisplay.icon} className="mr-1" />
                        {typeDisplay.label}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <p className="text-gray-900 text-base leading-relaxed">
                      {nudge.message}
                    </p>
                  </div>

                  <div className="text-sm text-gray-600">
                    <div className="flex flex-wrap gap-4">
                      {nudge.projectName && (
                        <span>
                          <strong>Project:</strong> {nudge.projectName}
                        </span>
                      )}
                      {nudge.taskTitle && (
                        <span>
                          <strong>Task:</strong> {nudge.taskTitle}
                        </span>
                      )}
                    </div>
                  </div>

                  {nudge.link && (
                    <AppButton
                      variant="primary"
                      href={nudge.link}
                      inNewWindow={true}
                      className="mt-3"
                    >
                      Go to Todo <FontAwesomeIcon icon={faExternalLinkAlt} className="text-sm ml-3" />
                    </AppButton>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SimplePage>
    </ProtectedRoute>
  );
}